/**
 * Persistent Data Storage via IndexedDB
 * 
 * Unlike localStorage (which gets cleared on fitnessand.me), IndexedDB is a
 * completely separate storage engine that survives page refreshes reliably.
 * 
 * This module provides a simple key-value API for storing JSON data in IndexedDB.
 */

const DB_NAME = 'fitness_data_db';
const STORE_NAME = 'app_data';
const DB_VERSION = 1;

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function idbSave(key: string, value: any): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put(JSON.stringify(value), key);
    await new Promise<void>((res, rej) => {
      tx.oncomplete = () => res();
      tx.onerror = () => rej(tx.error);
    });
    db.close();
  } catch (e) {
    console.warn('[PersistentStorage] IDB save failed:', e);
  }
}

export async function idbLoad<T = any>(key: string): Promise<T | null> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(key);
    const result = await new Promise<string | null>((res, rej) => {
      req.onsuccess = () => res(req.result || null);
      req.onerror = () => rej(req.error);
    });
    db.close();
    return result ? JSON.parse(result) : null;
  } catch (e) {
    console.warn('[PersistentStorage] IDB load failed:', e);
    return null;
  }
}

export async function idbDelete(key: string): Promise<void> {
  try {
    const db = await openDB();
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(key);
    await new Promise<void>((res, rej) => {
      tx.oncomplete = () => res();
      tx.onerror = () => rej(tx.error);
    });
    db.close();
  } catch (e) {
    console.warn('[PersistentStorage] IDB delete failed:', e);
  }
}
